/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React, { CSSProperties } from "react";
import SvgIcon from "./SvgIcon";
import IMask from "imask";
import { IMaskInput } from "react-imask";
import { MaskedElement, ReactMaskProps } from "react-imask/dist/mixin";

export type Falsy = false | 0 | "" | null | undefined;
export type InputMaskProps<Opts, Unmask, Value> = MaskedElement &
  ReactMaskProps<Opts, Unmask, Value> & {
    label?: string;
    info?: string;
    required?: boolean;
    errorMessage?: string;
  };

const leftTextStyle: CSSProperties = {
  flex: 1,
  alignItems: "center",
  fontWeight: "bold",
  lineHeight: 1.5,
  padding: 4,
  backgroundColor: "#e9ecef",
  border: "1px solid #ced4da"
};

const InputMask = React.forwardRef(
  <
    Opts extends IMask.AnyMaskedOptions = IMask.AnyMaskedOptions,
    Unmask extends "typed" | boolean = false,
    Value = Unmask extends "typed"
      ? IMask.InputMask<Opts>["typedValue"]
      : Unmask extends Falsy
      ? IMask.InputMask<Opts>["value"]
      : IMask.InputMask<Opts>["unmaskedValue"]
  >(
    props: InputMaskProps<Opts, Unmask, Value>,
    ref
  ) => {
    const { label, info, required, errorMessage, ...rest } = props;
    const invalid = !!errorMessage;

    return (
      <div className={`mb-3 ${invalid && "field-group--invalid"}`}>
        {label && <label className="label">{label}</label>}
        {required && <sup style={{ color: "red" }}>*</sup>}
        {info && (
          <span
            className="help"
            data-bs-toggle="tooltip"
            data-trigger="hover"
            data-placement="right"
            data-html="true"
            data-container="body"
            title={info}
          >
            <SvgIcon name="question-circle" className="ml-1 label" />
          </span>
        )}

        <div className="d-flex">
          <span style={leftTextStyle}>P</span>
          <IMaskInput aria-invalid={invalid ? "true" : "false"} className="field" inputRef={ref} {...rest} />
        </div>

        {invalid && (
          <div className="field-helper">
            <label>{errorMessage}</label>
          </div>
        )}
      </div>
    );
  }
);

InputMask.displayName = "InputMask";

export default InputMask;
